import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import Layout from "../components/Layout";
import { FileText, Euro, Clock, CheckCircle2, Send, Plus } from "lucide-react";
import { Button } from "../components/ui/button";

const fmt = (n) => new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" }).format(n || 0);

function StatCard({ icon: Icon, label, value, accent }) {
  return (
    <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-5 transition-shadow hover:shadow-md">
      <div className="flex items-center justify-between">
        <span className="text-xs font-bold uppercase tracking-wider text-slate-500">{label}</span>
        <Icon className={`w-5 h-5 ${accent}`} strokeWidth={1.75} />
      </div>
      <p className="font-heading text-3xl font-black text-slate-900 mt-3" data-testid={`stat-${label}`}>{value}</p>
    </div>
  );
}

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [recent, setRecent] = useState([]);

  useEffect(() => {
    api.get("/quotes/stats/overview").then((r) => setStats(r.data)).catch(() => {});
    api.get("/quotes").then((r) => setRecent(r.data.slice(0, 5))).catch(() => {});
  }, []);

  const statusBadge = {
    draft: "bg-slate-100 text-slate-600",
    sent: "bg-amber-100 text-amber-700",
    signed: "bg-emerald-100 text-emerald-700",
  };
  const statusLabel = { draft: "Bozza", sent: "Inviato", signed: "Firmato" };

  return (
    <Layout>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-heading text-3xl font-black tracking-tight text-slate-900">Dashboard</h1>
          <p className="text-slate-500 mt-1">Panoramica dei tuoi preventivi.</p>
        </div>
        <Link to="/quotes/new">
          <Button data-testid="dashboard-new-quote" className="bg-blue-600 hover:bg-blue-700">
            <Plus className="w-4 h-4 mr-2" /> Nuovo
          </Button>
        </Link>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 mb-8">
        <StatCard icon={FileText} label="Preventivi" value={stats?.total_quotes ?? "—"} accent="text-blue-600" />
        <StatCard icon={Euro} label="Valore Totale" value={stats ? fmt(stats.total_value) : "—"} accent="text-emerald-600" />
        <StatCard icon={Clock} label="In Bozza" value={stats?.by_status?.draft ?? "—"} accent="text-slate-500" />
        <StatCard icon={CheckCircle2} label="Firmati" value={stats?.by_status?.signed ?? "—"} accent="text-emerald-600" />
      </div>

      <div className="bg-white border border-slate-200 rounded-lg shadow-sm">
        <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
          <h2 className="font-heading text-lg font-bold text-slate-900">Preventivi recenti</h2>
          <Link to="/quotes" className="text-sm text-blue-600 font-semibold hover:underline">Vedi tutti</Link>
        </div>
        {recent.length === 0 ? (
          <div className="p-10 text-center text-slate-500">
            <Send className="w-8 h-8 mx-auto mb-3 text-slate-300" />
            Nessun preventivo. Creane uno per iniziare.
          </div>
        ) : (
          <ul className="divide-y divide-slate-100">
            {recent.map((q) => (
              <li key={q.id}>
                <Link to={`/quotes/${q.id}`} className="flex items-center justify-between px-5 py-4 hover:bg-slate-50 transition-colors"
                  data-testid={`recent-quote-${q.id}`}>
                  <div>
                    <p className="font-semibold text-slate-900">{q.title}</p>
                    <p className="text-sm text-slate-500">{q.number} · {q.client_name || "Senza cliente"}</p>
                  </div>
                  <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${statusBadge[q.status]}`}>
                    {statusLabel[q.status]}
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </div>
    </Layout>
  );
}
